import { AEDFlag, BHDFlag, EGFlag, KWDFlag, OMRFlag, QARFlag, SAFlag } from '@assets/svgs/flags'
import { SelectChangeEvent } from '@mui/material/Select'
import React, { useState } from 'react'
import LanguageCurrencyDropdown, { CountryOption, LanguageCurrencyDropdownProps } from './LanguageCurrencyDropdown'
import { setCookie } from '@/src/utils/browser'

const getFlyinCountryOptions = (t: (key: string) => string): CountryOption[] => [
    { code: 'EG', label: t('countries.egypt'), domain: 'eg.flyin.com', qaDomain: 'meeg.flyin.com', Flag: EGFlag },
    { code: 'UAE', label: t('countries.uae'), domain: 'ae.flyin.com', qaDomain: 'meae.flyin.com', Flag: AEDFlag },
    { code: 'GLOBAL', label: t('countries.global'), domain: 'www.flyin.com', qaDomain: 'me.flyin.com' }
]

// CT variant: matches desktop-next-js Countries/data.ts (UAE, Oman, Qatar, Bahrain, Kuwait, Saudi Arabia)
const getCTCountryOptions = (t: (key: string) => string): CountryOption[] => [
    { code: 'AE', label: t('countries.uae'), domain: 'www.cleartrip.ae', qaDomain: 'me.cleartrip.ae', Flag: AEDFlag },
    { code: 'OM', label: t('countries.oman'), domain: 'www.cleartrip.om', qaDomain: 'me.cleartrip.om', Flag: OMRFlag },
    { code: 'QA', label: t('countries.qatar'), domain: 'www.cleartrip.qa', qaDomain: 'me.cleartrip.qa', Flag: QARFlag },
    { code: 'BH', label: t('countries.bahrain'), domain: 'www.cleartrip.bh', qaDomain: 'me.cleartrip.bh', Flag: BHDFlag },
    { code: 'KW', label: t('countries.kuwait'), domain: 'www.cleartrip.com.kw', qaDomain: 'me.cleartrip.com.kw', Flag: KWDFlag },
    { code: 'SA', label: t('countries.saudiArabia'), domain: 'www.cleartrip.sa', qaDomain: 'me.cleartrip.sa', Flag: SAFlag }
]

const detectCurrentCountry = (
    hostname: string,
    countryOptions: CountryOption[],
    isQA: boolean,
    variant: 'flyin' | 'cleartrip'
): CountryOption => {
    const matchedCountry = countryOptions.find(country => {
        const targetDomain = isQA ? country.qaDomain : country.domain
        return hostname.includes(targetDomain)
    })
    // Flyin: fallback to GLOBAL (index 2); CT: fallback to first option (e.g. AE)
    return matchedCountry || (variant === 'flyin' ? countryOptions[2] : countryOptions[0])
}
interface LanguageCurrencyDropdownLogicProps {
    selectedLanguage: LanguageCurrencyDropdownProps['selectedLanguage']
    selectedCurrency: LanguageCurrencyDropdownProps['selectedCurrency']
    languageOptions: LanguageCurrencyDropdownProps['languageOptions']
    currencyOptions: LanguageCurrencyDropdownProps['currencyOptions']
    onLanguageSelect: LanguageCurrencyDropdownProps['onLanguageSelect']
    onCurrencySelect: LanguageCurrencyDropdownProps['onCurrencySelect']
    t: LanguageCurrencyDropdownProps['t']
    tCurrency: LanguageCurrencyDropdownProps['tCurrency']
    isOffersPage?: boolean
    variant?: 'flyin' | 'cleartrip'
}

const LanguageCurrencyDropdownLogic: React.FC<LanguageCurrencyDropdownLogicProps> = props => {
    const variant = props.variant ?? 'flyin'
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [countrySelectOpen, setCountrySelectOpen] = useState(false)
    const open = Boolean(anchorEl)
    const isQA =
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('me') ||
            window.location.hostname.includes('qa2') ||
            window.location.hostname.startsWith('qa.'))
    const countryOptions = variant === 'cleartrip' ? getCTCountryOptions(props.t) : getFlyinCountryOptions(props.t)

    const getCurrentCountry = (): CountryOption => {
        if (typeof window === 'undefined') return countryOptions[0]

        const currentHostname = window.location.hostname
        return detectCurrentCountry(currentHostname, countryOptions, isQA, variant)
    }

    const [selectedCountry, setSelectedCountry] = useState<CountryOption>(() => {
        const country = getCurrentCountry()
        return country || countryOptions[0]
    })

    const handleCountryChange = (event: SelectChangeEvent) => {
        const countryCode = event.target.value
        const newCountry = countryOptions.find(c => c.code === countryCode) || countryOptions[0]

        if (newCountry.code === selectedCountry.code) return

        setSelectedCountry(newCountry)
        if (variant === 'flyin') {
            setCookie('cc', newCountry.code, undefined, '.flyin.com')
        }
        const targetDomain = isQA ? newCountry.qaDomain : newCountry.domain
        const currentUrl = new URL(window.location.href)
        currentUrl.hostname = targetDomain
        currentUrl.protocol = window.location.protocol
        window.location.href = currentUrl.toString()
    }

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleCountrySelectOpen = () => setCountrySelectOpen(true)
    const handleCountrySelectClose = () => setCountrySelectOpen(false)

    return (
        <LanguageCurrencyDropdown
            {...props}
            selectedCountry={selectedCountry}
            countryOptions={countryOptions}
            countrySelectOpen={countrySelectOpen}
            onCountrySelectOpen={handleCountrySelectOpen}
            onCountrySelectClose={handleCountrySelectClose}
            onCountryChange={handleCountryChange}
            onCountrySelect={() => {}}
            open={open}
            anchorEl={anchorEl}
            onOpen={handleOpen}
            onClose={handleClose}
            isOffersPage={props.isOffersPage}
        />
    )
}

export default LanguageCurrencyDropdownLogic
